/**
 * The print-house look of GoMake (not a browser setting, and not the brand theme).
 * - "modern"  : the new design system — the default when the print house has not chosen.
 * - "classic" : the previous GoMake stylesheet.
 *
 * Stored as the UI_STYLESHEET print-house preference. HP print houses always use the
 * hp-client brand theme; this preference does not apply there.
 */
export const UI_STYLESHEET_CODE = "UI_STYLESHEET";
export const UI_STYLESHEET_MODERN = "modern";
export const UI_STYLESHEET_CLASSIC = "classic";
/** Removed browser key. Cleared once so an old device setting cannot override the print house. */
const LEGACY_UI_STYLESHEET_STORAGE_KEY = "uiStylesheet";

export type UiStylesheet =
  | typeof UI_STYLESHEET_MODERN
  | typeof UI_STYLESHEET_CLASSIC;

export const isUiStylesheet = (value?: string | null): value is UiStylesheet =>
  value === UI_STYLESHEET_MODERN || value === UI_STYLESHEET_CLASSIC;

/** Drop the old per-browser stylesheet value. The print house preference is the source. */
export const clearLegacyUiStylesheetStorage = (): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(LEGACY_UI_STYLESHEET_STORAGE_KEY);
  } catch {
    // ignore private-mode storage errors
  }
};

type PreferenceLike = {
  code?: string;
  Code?: string;
  value?: string;
  Value?: string;
  valueKey?: string;
  ValueKey?: string;
  preferenceValues?: Array<{ id?: string; Id?: string; key?: string; Key?: string }>;
  PreferenceValues?: Array<{ id?: string; Id?: string; key?: string; Key?: string }>;
};

/**
 * Reads UI_STYLESHEET from the print house preference list returned with the profile.
 * Missing or unknown values stay on modern.
 */
export const stylesheetFromPreferences = (
  preferences: unknown,
): UiStylesheet => {
  if (!Array.isArray(preferences)) return UI_STYLESHEET_MODERN;
  const preference = (preferences as PreferenceLike[]).find(
    (item) => (item?.code ?? item?.Code) === UI_STYLESHEET_CODE,
  );
  if (!preference) return UI_STYLESHEET_MODERN;

  const valueKey = preference.valueKey ?? preference.ValueKey;
  if (isUiStylesheet(valueKey)) return valueKey;

  const value = String(preference.value ?? preference.Value ?? "");
  const options =
    preference.preferenceValues ?? preference.PreferenceValues ?? [];
  const match = options.find(
    (option) => String(option?.id ?? option?.Id ?? "") === value,
  );
  const key = match?.key ?? match?.Key;
  return isUiStylesheet(key) ? key : UI_STYLESHEET_MODERN;
};

/** Theme name the stylesheet preference maps to (`null` = base/classic theme). */
export const uiStylesheetToTheme = (value: UiStylesheet): string | null =>
  value === UI_STYLESHEET_CLASSIC ? null : UI_STYLESHEET_MODERN;
